
import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { ArrowLeft, Mic, MicOff, Loader2, TrendingDown, TrendingUp, Check, Camera } from "lucide-react"
import { toast } from "sonner"
import { useVoiceRecognition } from "@/hooks/use-voice-recognition"
import { useAuth } from "@/lib/auth/auth-context"
import { useTransactions } from "@/lib/context/transaction-context"
import { useAccountSelection } from "@/lib/context/account-selection-context"
import { fetchUserCategories } from "@/lib/services/categories"
import { fetchPrimarySubAccount } from "@/lib/services/sub-accounts"
import { fetchUserCards } from "@/lib/services/cards"
import type { CustomCategory, PaymentCard } from "@/lib/types"

interface AddExpenseProps {
  onClose: () => void
}

export function AddExpense({ onClose }: AddExpenseProps) {
  const { user } = useAuth()
  const { refreshTransactions } = useTransactions()
  const { selectedAccountId } = useAccountSelection()
  const [amount, setAmount] = useState("")
  const [description, setDescription] = useState("")
  const [selectedCategoryId, setSelectedCategoryId] = useState("")
  const [type, setType] = useState<"expense" | "income">("expense")
  const [date, setDate] = useState(new Date().toISOString().split("T")[0])
  const [isSaving, setIsSaving] = useState(false)
  const [quickSaveMode, setQuickSaveMode] = useState(() => {
    // Load Quick Save preference from localStorage
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("expense-tracker-quick-save")
      return saved === "true"
    }
    return true
  })
  const [isParsingVoice, setIsParsingVoice] = useState(false)
  const [isQuickSaving, setIsQuickSaving] = useState(false)
  const [quickSaveTrigger, setQuickSaveTrigger] = useState(0) // Trigger to force useEffect execution
  const voiceModeRef = useRef<"full" | "description" | null>(null)
  const [isListeningVisual, setIsListeningVisual] = useState(false)
  const isListeningRef = useRef(false) // Synchronous listening state guard
  const clickDebounceRef = useRef(false) // Debounce protection
  const isAIParsingRef = useRef(false) // Flag to prevent category reset during AI parsing
  const handleSaveRef = useRef<((fromVoiceInput?: boolean) => Promise<void>) | null>(null) // Ref to avoid circular dependency
  const quickSavePendingRef = useRef(false) // Flag to trigger quick save when state is ready
  const quickSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null) // Timeout for quick save safety
  const [expenseCategories, setExpenseCategories] = useState<CustomCategory[]>([])
  const [incomeCategories, setIncomeCategories] = useState<CustomCategory[]>([])
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [smartTransactionText, setSmartTransactionText] = useState("")
  const [isProcessingReceipt, setIsProcessingReceipt] = useState(false)
  const receiptInputRef = useRef<HTMLInputElement>(null)
  const [primarySubAccountId, setPrimarySubAccountId] = useState<string | null>(null)
  const [paymentCards, setPaymentCards] = useState<PaymentCard[]>([])
  const [selectedCardId, setSelectedCardId] = useState<string>("")
  const [loadingCards, setLoadingCards] = useState(false)

  // Load categories from Supabase
  useEffect(() => {
    const loadCategories = async () => {
      if (!user) return

      try {
        setLoadingCategories(true)
        const [expenses, incomes] = await Promise.all([
          fetchUserCategories(user.id, 'expense'),
          fetchUserCategories(user.id, 'income')
        ])
        setExpenseCategories(expenses)
        setIncomeCategories(incomes)
      } catch (error) {
        console.error('Error loading categories:', error)
        toast.error('Failed to load categories')
      } finally {
        setLoadingCategories(false)
      }
    }

    loadCategories()
  }, [user])

  // Load primary sub-account for default transaction assignment (if no account selected in dashboard)
  useEffect(() => {
    const loadPrimarySubAccount = async () => {
      if (!user) return

      try {
        const primaryAccount = await fetchPrimarySubAccount(user.id)
        if (primaryAccount) {
          setPrimarySubAccountId(primaryAccount.id)
        }
      } catch (error) {
        console.error('Error loading primary sub-account:', error)
        // Not critical - transactions can still be created without sub-account
      }
    }

    loadPrimarySubAccount()
  }, [user])

  // Load payment cards filtered by selected sub-account
  useEffect(() => {
    const loadPaymentCards = async () => {
      if (!user) return

      try {
        setLoadingCards(true)
        // Use selectedAccountId from context, convert "all" to null, fallback to primary if not set
        const accountId = selectedAccountId === "all" ? null : selectedAccountId
        const finalAccountId = accountId || primarySubAccountId

        console.log('💳 [ADD-EXPENSE] Loading cards for account:', {
          selectedAccountId,
          primarySubAccountId,
          finalAccountId,
          timestamp: new Date().toISOString()
        })

        const cards = await fetchUserCards(user.id, finalAccountId)

        console.log('💳 [ADD-EXPENSE] Cards loaded:', {
          count: cards.length,
          cards: cards.map(c => ({ id: c.id, nickname: c.nickname, lastFour: c.lastFour })),
          timestamp: new Date().toISOString()
        })

        setPaymentCards(cards)
        // Auto-select first card if available
        if (cards.length > 0 && !selectedCardId) {
          setSelectedCardId(cards[0].id)
          console.log('💳 [ADD-EXPENSE] Auto-selected card:', cards[0].id)
        } else if (cards.length === 0) {
          setSelectedCardId("")
          console.log('💳 [ADD-EXPENSE] No cards available for this account')
        }
      } catch (error) {
        console.error('Error loading payment cards:', error)
        toast.error('Failed to load payment cards')
      } finally {
        setLoadingCards(false)
      }
    }

    loadPaymentCards()
  }, [user, selectedAccountId, primarySubAccountId, selectedCardId])

  // Dynamic category selection based on transaction type
  const currentCategories = useMemo(() => {
    return type === "expense" ? expenseCategories : incomeCategories
  }, [type, expenseCategories, incomeCategories])

  // Parse voice transaction with Gemini API
  const parseVoiceTransaction = useCallback(async (transcript: string) => {
    const startTime = Date.now()
    setIsParsingVoice(true)
    setIsListeningVisual(false) // Stop showing listening state
    isAIParsingRef.current = true // Set flag to prevent category reset during parsing

    // Send all categories (both expense and income) so Gemini can properly categorize
    const allCategories = [
      ...expenseCategories.map((c) => `${c.name} (expense)`),
      ...incomeCategories.map((c) => `${c.name} (income)`)
    ]

    const requestPayload = {
      transcript,
      categories: allCategories,
      type, // Current UI state, but Gemini will override if needed
    }

    console.log('🤖 [API] Gemini Request Starting', {
      timestamp: new Date().toISOString(),
      payload: requestPayload
    })

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

      const response = await fetch(`${supabaseUrl}/functions/v1/parse-transaction`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify(requestPayload),
      })

      const elapsed = Date.now() - startTime
      console.log('🤖 [API] Response Received', {
        timestamp: new Date().toISOString(),
        status: response.status,
        ok: response.ok,
        elapsedMs: elapsed
      })

      if (!response.ok) throw new Error("Failed to parse transaction")

      const data = await response.json()
      console.log('🤖 [API] Response Parsed', {
        timestamp: new Date().toISOString(),
        data,
        elapsedMs: elapsed
      })

      // Auto-fill fields
      console.log('📊 [DATA] Field Extraction Starting', {
        timestamp: new Date().toISOString(),
        rawData: data
      })

      // IMPORTANT: Set type first so category list updates correctly
      if (data.type && (data.type === "expense" || data.type === "income")) {
        const inferredType = data.type
        if (inferredType !== type) {
          console.log('🔄 [TYPE] Auto-switching transaction type', {
            timestamp: new Date().toISOString(),
            from: type,
            to: inferredType,
            reason: 'Gemini AI inference'
          })
          setType(inferredType)
        } else {
          console.log('✅ [TYPE] Type matches current selection', {
            timestamp: new Date().toISOString(),
            type: inferredType
          })
        }
      }

      if (data.amount) {
        const parsedAmount = parseFloat(data.amount)
        if (!isNaN(parsedAmount) && parsedAmount > 0) {
          // Format to 2 decimal places for consistent display
          const formattedAmount = parsedAmount.toFixed(2)
          console.log('✅ [DATA] Amount extracted successfully', {
            timestamp: new Date().toISOString(),
            raw: data.amount,
            parsed: parsedAmount,
            formatted: formattedAmount
          })
          setAmount(formattedAmount)
        } else {
          console.error('❌ [DATA] Invalid amount from AI', {
            timestamp: new Date().toISOString(),
            raw: data.amount,
            parsed: parsedAmount,
            isNaN: isNaN(parsedAmount),
            reason: isNaN(parsedAmount) ? 'Not a number' : 'Zero or negative'
          })
          toast.error(`Could not parse amount "${data.amount}". Please enter manually.`)
        }
      } else {
        console.log('⚠️ [DATA] No amount in AI response', {
          timestamp: new Date().toISOString()
        })
        toast.warning('No amount detected. Please enter manually.')
      }

      if (data.description) {
        console.log('📊 [DATA] Extracting Description', {
          timestamp: new Date().toISOString(),
          value: data.description
        })
        setDescription(data.description)
      } else {
        console.log('📊 [DATA] Description Skipped', {
          timestamp: new Date().toISOString(),
          reason: 'Empty description'
        })
      }

      if (data.category) {
        // Validate category against the correct category list for the inferred type
        const categoriesForType = data.type === "expense" ? expenseCategories : incomeCategories
        const matchedCategory = categoriesForType.find((c) => c.name === data.category)

        if (matchedCategory) {
          console.log('📊 [DATA] Extracting Category', {
            timestamp: new Date().toISOString(),
            value: data.category,
            categoryId: matchedCategory.id,
            inferredType: data.type,
            currentType: type,
            validated: true
          })
          setSelectedCategoryId(matchedCategory.id)
        } else {
          console.log('⚠️ [DATA] Category Invalid for Type', {
            timestamp: new Date().toISOString(),
            category: data.category,
            inferredType: data.type,
            availableCategories: categoriesForType.map(c => c.name),
            reason: 'Category not found in type category list'
          })
          setSelectedCategoryId("") // Clear invalid category
        }
      } else {
        console.log('📊 [DATA] Category Skipped', {
          timestamp: new Date().toISOString(),
          reason: 'Empty category'
        })
      }

      voiceModeRef.current = null
      console.log('✅ [SUCCESS] Transaction Parsed', {
        timestamp: new Date().toISOString(),
        amount: data.amount,
        description: data.description,
        category: data.category,
        totalElapsedMs: Date.now() - startTime
      })

      // Auto-save if quick save is enabled
      const shouldQuickSave = quickSaveMode && data.amount && parseFloat(data.amount) > 0 && data.category

      console.log('🔄 [QUICK_SAVE] Decision point', {
        timestamp: new Date().toISOString(),
        quickSaveMode,
        conditions: {
          quickSaveEnabled: quickSaveMode,
          hasAmount: !!data.amount,
          amountValid: data.amount && parseFloat(data.amount) > 0,
          hasCategory: !!data.category,
          shouldQuickSave
        },
        aiResponse: {
          amount: data.amount,
          category: data.category,
          description: data.description,
          type: data.type
        }
      })

      if (shouldQuickSave) {
        console.log('🔄 [QUICK_SAVE] Pending flag set - waiting for React state update')

        // Set flag to indicate we're waiting for state to sync
        quickSavePendingRef.current = true

        // Increment trigger to force useEffect execution even if values don't change
        setQuickSaveTrigger(prev => prev + 1)

        // Safety timeout: cancel quick save if state doesn't sync within 2 seconds
        if (quickSaveTimeoutRef.current) {
          clearTimeout(quickSaveTimeoutRef.current)
        }
        quickSaveTimeoutRef.current = setTimeout(() => {
          if (quickSavePendingRef.current) {
            console.error('❌ [QUICK_SAVE] State sync timeout after 2 seconds')
            quickSavePendingRef.current = false
            toast.error('Could not auto-save. Please check fields and save manually.')
            isAIParsingRef.current = false
          }
        }, 2000)
      } else {
        // Quick Save disabled - show parsing success toast
        toast.success("Transaction parsed successfully!")
        console.log('🔄 [QUICK_SAVE] Skipped', {
          timestamp: new Date().toISOString(),
          reason: !quickSaveMode ? 'Quick Save disabled' : 'Missing required fields',
          quickSaveMode,
          hasAmount: !!(data.amount && parseFloat(data.amount) > 0),
          hasCategory: !!data.category
        })
        // Clear AI parsing flag since we're not auto-saving
        isAIParsingRef.current = false
      }
    } catch (error) {
      console.error('❌ [API] Gemini Error', {
        timestamp: new Date().toISOString(),
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        elapsedMs: Date.now() - startTime
      })
      toast.error("Failed to parse voice input. Please try again.")
      // Clear AI parsing flag on error
      isAIParsingRef.current = false
    } finally {
      setIsParsingVoice(false)
      voiceModeRef.current = null
      console.log('🔄 [STATE] Cleanup Complete', {
        timestamp: new Date().toISOString(),
        voiceMode: null,
        isParsingVoice: false
      })
    }
  }, [type, quickSaveMode, expenseCategories, incomeCategories])

  // Memoized voice recognition callbacks to prevent useEffect re-runs
  const handleVoiceResult = useCallback(async (result: { transcript: string; confidence: number }) => {
    const mode = voiceModeRef.current
    console.log('🔄 [CALLBACK] Voice Result Received', {
      timestamp: new Date().toISOString(),
      mode,
      transcript: result.transcript,
      confidence: result.confidence
    })

    if (mode === "description") {
      // Description-only mode: just set the description
      console.log('📝 [MODE] Description-Only Processing', {
        timestamp: new Date().toISOString(),
        transcript: result.transcript
      })
      setDescription(result.transcript)
      isListeningRef.current = false // Sync ref state
      setIsListeningVisual(false)
      voiceModeRef.current = null
      toast.success("Description captured!")
    } else if (mode === "full") {
      // Full transaction mode: populate text input and parse with Gemini
      console.log('🤖 [MODE] Full Transaction Processing', {
        timestamp: new Date().toISOString(),
        transcript: result.transcript,
        willCallAPI: true
      })
      setSmartTransactionText(result.transcript) // Populate text input with transcript
      isListeningRef.current = false // Sync ref state
      setIsListeningVisual(false)
      await parseVoiceTransaction(result.transcript)
    } else {
      console.warn('⚠️ [CALLBACK] Unknown Mode', {
        timestamp: new Date().toISOString(),
        mode,
        transcript: result.transcript
      })
    }
  }, [parseVoiceTransaction])

  const handleVoiceError = useCallback((error: string) => {
    console.error('❌ [CALLBACK] Voice Error Handler', {
      timestamp: new Date().toISOString(),
      error
    })
    toast.error(error)
    isListeningRef.current = false // Sync ref state
    setIsListeningVisual(false)
    voiceModeRef.current = null
    setIsParsingVoice(false)
  }, [])

  // Voice recognition hook
  const voiceRecognition = useVoiceRecognition({
    lang: "en-US",
    continuous: false,
    interimResults: true,
    onResult: handleVoiceResult,
    onError: handleVoiceError,
  })

  // Track voice-to-text transcription state (Whisper API processing)
  const isTranscribing = voiceRecognition.state === "processing"

  // Handle full transaction voice input
  const handleFullVoiceInput = () => {
    // Debounce protection - prevent rapid clicks
    if (clickDebounceRef.current) return
    clickDebounceRef.current = true
    setTimeout(() => {
      clickDebounceRef.current = false
    }, 500)

    console.log('🎤 [VOICE] Full Transaction Button Clicked', {
      timestamp: new Date().toISOString(),
      isSupported: voiceRecognition.isSupported,
      isCurrentlyListening: isListeningRef.current,
      currentType: type,
      categoriesCount: currentCategories.length
    })

    // If currently listening in full mode, stop recording (check ref for synchronous state)
    if (isListeningRef.current && voiceModeRef.current === "full") {
      console.log('🛑 [VOICE] Manual Stop Requested', {
        timestamp: new Date().toISOString(),
        mode: "full"
      })
      isListeningRef.current = false
      setIsListeningVisual(false)
      voiceRecognition.forceStop() // Use force stop to bypass state checks
      return
    }

    if (!voiceRecognition.isSupported) {
      console.warn('⚠️ [VOICE] Browser Not Supported')

      // Check if it's a secure context issue
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        const isLocalIP = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.)/.test(window.location.hostname)
        if (isLocalIP) {
          toast.error("Voice input requires HTTPS or localhost. Please use http://localhost:3000", { duration: 5000 })
        } else {
          toast.error("Voice input requires a secure connection (HTTPS or localhost)", { duration: 5000 })
        }
      } else {
        toast.error("Voice recognition is not supported in your browser")
      }
      return
    }
    isListeningRef.current = true
    voiceModeRef.current = "full"
    setIsListeningVisual(true)
    voiceRecognition.resetTranscript()
    voiceRecognition.startListening()
  }

  // Handle manual text processing
  const handleManualTextProcessing = async () => {
    if (!smartTransactionText.trim()) {
      toast.error("Please enter transaction details")
      return
    }

    console.log('📝 [MANUAL] Processing Text Input', {
      timestamp: new Date().toISOString(),
      text: smartTransactionText
    })

    await parseVoiceTransaction(smartTransactionText)
  }

  // Handle receipt scanning
  const handleReceiptScan = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file")
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB")
      return
    }

    setIsProcessingReceipt(true)
    isAIParsingRef.current = true // Prevent category reset during processing

    try {
      // Convert to base64
      const reader = new FileReader()
      reader.onload = async (event) => {
        const base64Image = event.target?.result as string

        console.log('📸 [RECEIPT] Processing Receipt Image', {
          timestamp: new Date().toISOString(),
          fileSize: file.size,
          fileType: file.type
        })

        // Send to API
        const allCategories = [
          ...expenseCategories.map((c) => `${c.name} (expense)`),
          ...incomeCategories.map((c) => `${c.name} (income)`)
        ]

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

        const response = await fetch(`${supabaseUrl}/functions/v1/parse-image`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${supabaseAnonKey}`
          },
          body: JSON.stringify({
            image: base64Image,
            categories: allCategories
          })
        })

        if (!response.ok) throw new Error("Failed to parse receipt")

        const result = await response.json()
        console.log('📸 [RECEIPT] Receipt Parsed', {
          timestamp: new Date().toISOString(),
          documentType: result.documentType,
          data: result.data
        })

        // Handle receipt data (single transaction)
        if (result.documentType === "receipt" && result.data) {
          const data = result.data

          // Set type first
          if (data.type === "expense" || data.type === "income") {
            setType(data.type)
          }

          // Set fields with validation
          if (data.amount) {
            const parsedAmount = parseFloat(data.amount)
            if (!isNaN(parsedAmount) && parsedAmount > 0) {
              setAmount(parsedAmount.toFixed(2))
            } else {
              console.error('❌ [RECEIPT] Invalid amount', {
                raw: data.amount,
                parsed: parsedAmount
              })
              toast.warning('Could not parse amount from receipt. Please enter manually.')
            }
          }
          if (data.description) {
            setDescription(data.description)
          }
          if (data.category) {
            const categoriesForType = data.type === "expense" ? expenseCategories : incomeCategories
            const matchedCategory = categoriesForType.find((c) => c.name === data.category)
            if (matchedCategory) {
              setSelectedCategoryId(matchedCategory.id)
            }
          }

          toast.success("Receipt scanned successfully!")
        } else {
          toast.error("Could not extract receipt data. Please try again.")
        }

        // Clear flag after processing
        setTimeout(() => {
          isAIParsingRef.current = false
        }, 500)
      }

      reader.onerror = () => {
        throw new Error("Failed to read image file")
      }

      reader.readAsDataURL(file)
    } catch (error) {
      console.error('📸 [RECEIPT] Error processing receipt', {
        timestamp: new Date().toISOString(),
        error
      })
      toast.error("Failed to process receipt. Please try again.")
      isAIParsingRef.current = false
    } finally {
      setIsProcessingReceipt(false)
      // Clear file input
      if (receiptInputRef.current) {
        receiptInputRef.current.value = ""
      }
    }
  }

  // Handle description-only voice input
  const handleDescriptionVoiceInput = () => {
    // Debounce protection - prevent rapid clicks
    if (clickDebounceRef.current) return
    clickDebounceRef.current = true
    setTimeout(() => {
      clickDebounceRef.current = false
    }, 500)

    console.log('🎤 [VOICE] Description Button Clicked', {
      timestamp: new Date().toISOString(),
      isSupported: voiceRecognition.isSupported,
      isCurrentlyListening: isListeningRef.current
    })

    // If currently listening in description mode, stop recording (check ref for synchronous state)
    if (isListeningRef.current && voiceModeRef.current === "description") {
      console.log('🛑 [VOICE] Manual Stop Requested', {
        timestamp: new Date().toISOString(),
        mode: "description"
      })
      isListeningRef.current = false
      setIsListeningVisual(false)
      voiceRecognition.forceStop() // Use force stop to bypass state checks
      return
    }

    if (!voiceRecognition.isSupported) {
      console.warn('⚠️ [VOICE] Browser Not Supported')

      // Check if it's a secure context issue
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        const isLocalIP = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.)/.test(window.location.hostname)
        if (isLocalIP) {
          toast.error("Voice input requires HTTPS or localhost. Please use http://localhost:3000", { duration: 5000 })
        } else {
          toast.error("Voice input requires a secure connection (HTTPS or localhost)", { duration: 5000 })
        }
      } else {
        toast.error("Voice recognition is not supported in your browser")
      }
      return
    }
    isListeningRef.current = true
    voiceModeRef.current = "description"
    setIsListeningVisual(true)
    voiceRecognition.resetTranscript()
    voiceRecognition.startListening()
  }


  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault()
        handleSave()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [amount, description, selectedCategoryId, type, date])

  // Save Quick Save preference to localStorage
  useEffect(() => {
    localStorage.setItem("expense-tracker-quick-save", quickSaveMode.toString())
  }, [quickSaveMode])

  // Reset category when switching type if current category is not valid for new type
  // Skip this during AI parsing to prevent race conditions
  useEffect(() => {
    // Don't reset category while AI is parsing and setting values
    if (isAIParsingRef.current) return

    const categoryExists = currentCategories.some((c) => c.id === selectedCategoryId)
    if (!categoryExists && selectedCategoryId) {
      setSelectedCategoryId("")
    }
  }, [type, currentCategories, selectedCategoryId])

  // Validate and sanitize amount input - only allow numeric values
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Allow empty string or valid numeric input (digits and single decimal point)
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setAmount(value)
    }
  }

  // Prevent non-numeric keyboard input
  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    if (
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "Tab" ||
      e.key === "Escape" ||
      e.key === "Enter" ||
      e.key === "." ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown"
    ) {
      return
    }

    // Block: e, E, +, -, and any non-digit characters
    if (!/^\d$/.test(e.key)) {
      e.preventDefault()
    }
  }

  const handleSave = useCallback(async (fromVoiceInput: boolean = false) => {
    console.log('💾 [SAVE] Called with state:', {
      timestamp: new Date().toISOString(),
      amount,
      selectedCategoryId,
      fromVoiceInput,
      quickSaveMode,
      isQuickSaving: fromVoiceInput && quickSaveMode
    })

    // Set quick saving state if this is from voice input with quick save enabled
    if (fromVoiceInput && quickSaveMode) {
      setIsQuickSaving(true)
    }

    // Validation
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount")
      if (fromVoiceInput && quickSaveMode) setIsQuickSaving(false)
      return
    }

    if (!selectedCategoryId) {
      toast.error("Please select a category")
      if (fromVoiceInput && quickSaveMode) setIsQuickSaving(false)
      return
    }

    setIsSaving(true)

    // Get category name for logging and description
    const selectedCategory = currentCategories.find(c => c.id === selectedCategoryId)
    const categoryName = selectedCategory?.name || 'Unknown'

    if (fromVoiceInput) {
      console.log('💾 [SAVE] Voice Input Auto-Save', {
        timestamp: new Date().toISOString(),
        amount,
        categoryId: selectedCategoryId,
        categoryName,
        type,
        quickSaveEnabled: quickSaveMode
      })
    }

    try {
      if (!user) {
        toast.error("You must be logged in to add transactions")
        if (fromVoiceInput && quickSaveMode) {
          setIsQuickSaving(false)
          isAIParsingRef.current = false
        }
        return
      }

      const { createClient } = await import('@/lib/supabase/client')
      const supabase = createClient()

      const tableName = type === "expense" ? "user_expenses" : "user_incomes"

      // Convert "all" to null for database insertion
      const accountId = selectedAccountId === "all" ? null : selectedAccountId
      const finalAccountId = accountId || primarySubAccountId

      // Debug: Log account and card selection
      console.log('💾 [ADD-EXPENSE] Saving transaction:', {
        selectedAccountId,
        primarySubAccountId,
        finalAccountId,
        selectedCardId,
        hasCards: paymentCards.length,
        cardDetails: paymentCards.length > 0 ? paymentCards[0] : null,
        timestamp: new Date().toISOString()
      })

      // Build insert data conditionally (user_incomes doesn't have category column)
      const insertData: any = {
        user_id: user.id,
        name: description || `${categoryName} ${type}`,
        amount: parseFloat(amount),
        frequency: 'one-time',
        created_at: new Date(date).toISOString(),
        sub_account_id: finalAccountId, // Use context selection or fallback to primary
        payment_card_id: selectedCardId || null, // Associate with selected payment card
      }

      // Only add category for expenses (incomes table doesn't have this column)
      // Store category name (TEXT) not ID (UUID) for database compatibility
      if (type === "expense") {
        insertData.category = categoryName
      }

      const { data, error } = await supabase
        .from(tableName)
        .insert(insertData)
        .select()

      if (error) {
        console.error('Supabase error:', error)
        toast.error(`Failed to save ${type}: ${error.message}`)
        setIsSaving(false)
        if (fromVoiceInput && quickSaveMode) {
          setIsQuickSaving(false)
          isAIParsingRef.current = false
        }
        return
      }

      console.log(`${type} saved successfully:`, data)

      // Different success message for quick save
      if (fromVoiceInput && quickSaveMode) {
        toast.success(`✨ Transaction auto-saved successfully!`)
      } else {
        toast.success(`${type === "expense" ? "Expense" : "Income"} added successfully!`)
      }

      // Trigger manual refresh (Realtime will also update, but this ensures immediate update)
      await refreshTransactions()

      // Close after short delay
      setTimeout(() => {
        onClose()
      }, 500)
    } catch (error) {
      console.error('Error saving transaction:', error)
      toast.error('Failed to save transaction. Please try again.')
    } finally {
      setIsSaving(false)
      // Always clear quick saving state and AI parsing flag in finally block
      if (fromVoiceInput && quickSaveMode) {
        setIsQuickSaving(false)
        isAIParsingRef.current = false
      }
    }
  }, [amount, selectedCategoryId, currentCategories, type, description, date, user, refreshTransactions, onClose])

  // Update the ref whenever handleSave changes
  useEffect(() => {
    handleSaveRef.current = handleSave
  }, [handleSave])

  // Quick Save: Trigger save when React state is updated
  useEffect(() => {
    // Only proceed if quick save is pending
    if (!quickSavePendingRef.current) return

    // Check if state has been populated with fresh values
    const isStateReady = amount && parseFloat(amount) > 0 && selectedCategoryId

    console.log('🔄 [QUICK_SAVE] useEffect triggered', {
      timestamp: new Date().toISOString(),
      quickSavePending: quickSavePendingRef.current,
      quickSaveTrigger,
      amount,
      selectedCategoryId,
      isStateReady
    })

    if (isStateReady) {
      // Clear the pending flag
      quickSavePendingRef.current = false

      // Clear the safety timeout
      if (quickSaveTimeoutRef.current) {
        clearTimeout(quickSaveTimeoutRef.current)
        quickSaveTimeoutRef.current = null
      }

      console.log('✅ [QUICK_SAVE] State ready, triggering save', {
        timestamp: new Date().toISOString(),
        amount,
        categoryId: selectedCategoryId,
        description,
        type
      })

      // Trigger save with fresh state values
      if (handleSaveRef.current) {
        handleSaveRef.current(true).catch((error) => {
          console.error('❌ [QUICK_SAVE] Save failed', {
            timestamp: new Date().toISOString(),
            error
          })
          toast.error('Quick save failed. Please save manually.')
          setIsQuickSaving(false)
          isAIParsingRef.current = false
        })
      } else {
        console.error('❌ [QUICK_SAVE] handleSaveRef is null')
        toast.error('Quick save failed. Please save manually.')
        setIsQuickSaving(false)
        isAIParsingRef.current = false
      }
    }
  }, [amount, selectedCategoryId, description, type, quickSaveTrigger])

  // Cleanup: Clear timeout on component unmount
  useEffect(() => {
    return () => {
      if (quickSaveTimeoutRef.current) {
        clearTimeout(quickSaveTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="fixed inset-0 bg-background z-[60] flex flex-col animate-in slide-in-from-bottom duration-300">
      {/* Sticky Header */}
      <div className="sticky top-0 bg-card border-b shadow-sm z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={onClose} disabled={isSaving}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Add Transaction</h1>
          <Button
            onClick={() => handleSave()}
            disabled={isSaving}
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg hover:shadow-xl transition-all px-6"
          >
            {isSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6 max-w-md mx-auto">
          {/* AI Voice Input & Quick Save - 50/50 Layout */}
          <div className="grid grid-cols-2 gap-3">
            {/* AI Voice Input - Left 50% */}
            <Button
              type="button"
              variant="outline"
              onClick={handleFullVoiceInput}
              disabled={!voiceRecognition.isSupported || isSaving || isTranscribing || isParsingVoice}
              className={`h-20 flex flex-col items-center justify-center gap-2 border-2 border-primary shadow-sm transition-all ${
                isListeningVisual && voiceModeRef.current === "full" ? "bg-red-500/10 ring-2 ring-red-500" : ""
              }`}
              title={
                isListeningVisual && voiceModeRef.current === "full"
                  ? "Click to stop recording"
                  : isTranscribing
                  ? "Converting speech to text..."
                  : isParsingVoice
                  ? "Analyzing transaction..."
                  : voiceRecognition.isSupported
                  ? "Speak full transaction"
                  : typeof window !== 'undefined' && !window.isSecureContext
                    ? "Voice requires HTTPS or localhost"
                    : "Voice not supported in this browser"
              }
            >
              {isListeningVisual && voiceModeRef.current === "full" ? (
                <>
                  <Mic className="h-6 w-6 text-red-500 animate-pulse" />
                  <span className="text-xs font-medium text-red-500">Recording...</span>
                </>
              ) : isTranscribing ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-xs font-medium text-primary">Transcribing...</span>
                </>
              ) : isParsingVoice ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-xs font-medium text-primary">Analyzing...</span>
                </>
              ) : (
                <>
                  <Mic className="h-6 w-6" />
                  <span className="text-xs font-medium">AI Voice Input</span>
                </>
              )}
            </Button>

            {/* Quick Save - Right 50% */}
            <div className="flex flex-col items-center justify-center p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between w-full">
                <Label htmlFor="quick-save" className="text-sm font-medium cursor-pointer">
                  Quick Save
                </Label>
                <Switch
                  id="quick-save"
                  checked={quickSaveMode}
                  onCheckedChange={setQuickSaveMode}
                  disabled={isSaving}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1 text-center">Auto-save when ready</p>
            </div>
          </div>

          {/* Smart Transaction Text Input */}
          <div className="space-y-2">
            <Label htmlFor="smart-text" className="text-base">Smart Transaction Input</Label>
            <div className="flex gap-2">
              <Input
                id="smart-text"
                placeholder="Spent $50 on groceries at Walmart"
                value={isListeningVisual && voiceModeRef.current === "full" ? voiceRecognition.transcript : smartTransactionText}
                onChange={(e) => setSmartTransactionText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && smartTransactionText.trim()) {
                    handleManualTextProcessing()
                  }
                }}
                className="flex-1 h-12 text-base"
                disabled={isSaving || isTranscribing || isParsingVoice || (isListeningVisual && voiceModeRef.current === "full")}
              />
              <Button
                type="button"
                onClick={handleManualTextProcessing}
                disabled={!smartTransactionText.trim() || isSaving || isTranscribing || isParsingVoice}
                className="h-12 px-4"
              >
                {isTranscribing ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="ml-2">Transcribing</span>
                  </>
                ) : isParsingVoice ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="ml-2">Analyzing</span>
                  </>
                ) : (
                  "Process"
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {isListeningVisual && voiceModeRef.current === "full"
                ? "🎤 Listening... Speak your transaction details"
                : "Type transaction details or use voice input above. Press Enter or click Process."
              }
            </p>
          </div>

          {/* Type Toggle */}
          <div className="flex gap-2 p-1 bg-muted rounded-lg">
            <Button
              variant={type === "expense" ? "default" : "ghost"}
              className="flex-1 h-12"
              onClick={() => setType("expense")}
              disabled={isSaving}
            >
              <TrendingDown className="h-4 w-4 mr-2 text-expense" />
              Expense
            </Button>
            <Button
              variant={type === "income" ? "default" : "ghost"}
              className="flex-1 h-12"
              onClick={() => setType("income")}
              disabled={isSaving}
            >
              <TrendingUp className="h-4 w-4 mr-2 text-income" />
              Income
            </Button>
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount" className="text-base">Amount</Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-muted-foreground">
                $
              </span>
              <Input
                id="amount"
                type="text"
                inputMode="decimal"
                pattern="^\d*\.?\d*$"
                placeholder="0.00"
                value={amount}
                onChange={handleAmountChange}
                onKeyDown={handleAmountKeyDown}
                className="pl-10 text-2xl font-bold h-16 text-center"
                disabled={isSaving}
                autoFocus
              />
            </div>
          </div>

          {/* Description with Receipt Scanner */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-base">Description</Label>
            <div className="relative">
              <Input
                id="description"
                placeholder="What was this for?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="pr-12 h-12 text-base"
                disabled={isSaving || isProcessingReceipt}
              />

              {/* Receipt Scanner Button */}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => receiptInputRef.current?.click()}
                disabled={isSaving || isProcessingReceipt}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                aria-label="Scan receipt"
                title="Scan receipt"
              >
                {isProcessingReceipt ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4" />
                )}
              </Button>

              {/* Hidden file input for receipt */}
              <input
                ref={receiptInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleReceiptScan}
                className="hidden"
              />
            </div>
          </div>

          {/* Category Selection */}
          <div className="space-y-4">
            <Label className="text-base">Category</Label>
            {loadingCategories ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-muted-foreground">Loading categories...</p>
              </div>
            ) : currentCategories.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-muted-foreground">No categories available. Create one in Settings.</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {currentCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategoryId(category.id)}
                    disabled={isSaving}
                    className={`flex flex-col items-center gap-2 p-3 sm:p-4 rounded-xl transition-all ${
                      selectedCategoryId === category.id
                        ? "ring-2 ring-primary bg-primary/5 scale-105"
                        : "hover:bg-muted active:scale-95"
                    } ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-xl ${category.color} flex items-center justify-center text-xl sm:text-2xl`}>
                      {category.icon}
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-center">{category.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label htmlFor="date" className="text-base">Date</Label>
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-12 text-base"
              disabled={isSaving}
            />
          </div>
        </div>
      </div>

      {/* Voice Recognition Processing Overlay */}
      {(isTranscribing || isParsingVoice || isQuickSaving) && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-background/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-card border shadow-2xl rounded-2xl p-8 max-w-sm mx-4 flex flex-col items-center gap-6 animate-in zoom-in-95 duration-300">
            {/* Animated Icon - Changes based on phase */}
            <div className="relative flex items-center justify-center">
              {isTranscribing ? (
                // Voice-to-Text Phase: Audio waveform animation
                <div className="flex items-center justify-center gap-1 h-16">
                  {[48, 32, 64, 40, 56].map((height, i) => (
                    <div
                      key={i}
                      className="w-2 bg-primary rounded-full animate-pulse"
                      style={{
                        height: `${height}px`,
                        animationDelay: `${i * 100}ms`,
                        animationDuration: '800ms'
                      }}
                    />
                  ))}
                </div>
              ) : isQuickSaving ? (
                // Saving Phase: Check mark with success animation
                <>
                  <div className="relative">
                    <Loader2 className="h-16 w-16 animate-spin text-green-500" />
                    <Check className="h-8 w-8 text-green-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                  <div className="absolute inset-0 h-16 w-16 rounded-full border-4 border-green-500/20 animate-pulse" />
                </>
              ) : (
                // AI Parsing Phase: Spinner with pulsing ring
                <>
                  <Loader2 className="h-16 w-16 animate-spin text-primary" />
                  <div className="absolute inset-0 h-16 w-16 rounded-full border-4 border-primary/20 animate-pulse" />
                </>
              )}
            </div>

            {/* Dynamic Status Message */}
            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold">
                {isTranscribing
                  ? "Converting Speech to Text"
                  : isQuickSaving
                  ? "Saving Transaction"
                  : "Analyzing Transaction"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isTranscribing
                  ? "Processing your voice recording with AI..."
                  : isQuickSaving
                  ? "Automatically saving your transaction..."
                  : "Extracting amount, category, and description..."
                }
              </p>
            </div>

            {/* Animated Loading Dots */}
            <div className="flex gap-2">
              <div
                className="h-2 w-2 rounded-full bg-primary animate-bounce"
                style={{ animationDelay: '0ms' }}
              />
              <div
                className="h-2 w-2 rounded-full bg-primary animate-bounce"
                style={{ animationDelay: '150ms' }}
              />
              <div
                className="h-2 w-2 rounded-full bg-primary animate-bounce"
                style={{ animationDelay: '300ms' }}
              />
            </div>

            {/* Progress indicator */}
            {isTranscribing && (
              <p className="text-xs text-muted-foreground">Step 1 of 3</p>
            )}
            {isParsingVoice && !isTranscribing && !isQuickSaving && (
              <p className="text-xs text-muted-foreground">Step 2 of 3</p>
            )}
            {isQuickSaving && !isTranscribing && !isParsingVoice && (
              <p className="text-xs text-muted-foreground">Step 3 of 3</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
